import { Card, Form, Select, Button, Spin } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";
import {
  getAvailabilitySlots,
  getAvailabilityTimezone,
  updateAvailabilityTimezone,
} from "../../actionCreator/availabilityActionCreator";
import { useAlert } from "../../context/AlertProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

const TimezoneSettings = ({ onTimezoneChange }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(true);
  const [currentTz, setCurrentTz] = useState();

  // ✅ Use moment to get timezone list (Day.js cannot list all zones)
  const timezones = moment.tz.names();

  useEffect(() => {
    // ✅ Fetch current timezone from backend
    const fetchData = async () => {
      try {
        const res = await dispatch(getAvailabilityTimezone());
        if (res?.data?.timezone) {
          setCurrentTz(res.data.timezone);
          form.setFieldsValue({ timezone: res.data.timezone });
          onTimezoneChange && onTimezoneChange(res.data.timezone);
        } else {
          form.setFieldsValue({ timezone: dayjs.tz.guess() });
          onTimezoneChange && onTimezoneChange(dayjs.tz.guess());
        }
      } catch (err) {
        console.error("Error fetching timezone:", err);
        form.setFieldsValue({ timezone: dayjs.tz.guess() });
        onTimezoneChange && onTimezoneChange(dayjs.tz.guess());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, form, onTimezoneChange]);

  const handleTimezoneSubmit = async (values) => {
    try {
      await dispatch(updateAvailabilityTimezone({ timezone: values.timezone }));
      showAlert("success", "Timezone updated successfully");
      dispatch(getAvailabilityTimezone());
      dispatch(getAvailabilitySlots());
      setCurrentTz(values.timezone);
      onTimezoneChange && onTimezoneChange(values.timezone);
    } catch (err) {
      showAlert("error", "Failed to update timezone");
      console.error("Error updating timezone:", err);
    }
  };

  return (
    <Card
      className="!tw-rounded-2xl !tw-border-slate-100 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
    >
      <div className="tw-mt-2 tw-mb-6">
        <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-1">Timezone Settings</h3>
        <p className="tw-text-xs tw-text-slate-500">
          Select your timezone for accurate scheduling and slots booking.
        </p>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ timezone: currentTz || dayjs.tz.guess() }}
          onFinish={handleTimezoneSubmit}
        >
          <Form.Item
            label={
              <span className="tw-text-sm tw-font-semibold tw-text-slate-600">
                Timezone
              </span>
            }
            name="timezone"
            rules={[{ required: true, message: "Please select a timezone" }]}
          >
            <Select
              showSearch
              placeholder="Select timezone"
              size="large"
              className="!tw-rounded-xl tw-w-full"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.value ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {timezones.map((tz) => (
                <Select.Option key={tz} value={tz}>
                  {tz}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item className="tw-flex tw-justify-end tw-mt-6 tw-mb-0">
            <Button
              type="primary"
              htmlType="submit"
              className="theme-btn tw-h-[40px] !tw-rounded-xl tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
            >
              Save Timezone
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );
};

export default TimezoneSettings;
